import { Component, effect, ElementRef, inject } from '@angular/core';
import { IdentityStore, IdentityStoreType } from '@appState';
import { DialogService, NavClickEvent, NavExpandNode } from 'cui-components';
import { MpnIdCheckDialogComponent } from 'src/app/app-pages/csp/mpn-id-check/mpn-id-check-dialog.component';
import { GenerateLicenseKeyDialogComponent } from 'src/app/app-pages/products-orders/empowered-licenses/generate-license-key/generate-license-key-dialog.component';
import { AppConfigService } from 'src/app/infrastructure/app-config.service';
import {
  setupActionsMenu,
  setupAuthorizationMenu,
  setupCommonDataMenu,
  setupCspMenu,
  setupFileManagementMenu,
  setupJobOrchestratorMenu,
  setupMarketingMenu,
  setupMonitoringMenu,
  setupNotificationsMenu,
  setupProductsAndOrdersMenu,
} from './permissions.helper';

@Component({
  selector: 'hui-authenticated-template-component',
  templateUrl: './authenticated-template.component.html',
  styleUrls: ['./authenticated-template.component.scss'],
  standalone: false,
})
export class AuthenticatedTemplateComponent {
  private identityStore: IdentityStoreType = inject(IdentityStore);
  private appConfig: AppConfigService = inject(AppConfigService);
  private dialogService: DialogService = inject(DialogService);
  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  isAuthenticated = this.identityStore.isAuthenticated;
  isSystemAdmin = this.identityStore.isSystemAdmin;
  userModel = this.identityStore.user;
  topBgColor = this.appConfig.config['FRONTEND_TOP_MENU_BG_COLOR'] ?? '#ff2f2f80';

  hasUserPermission = this.identityStore.hasPermissionForMenuAccess(['Portal.Admin.Users.MenuAccess']);
  hasAgreementPermission = this.identityStore.hasPermissionForMenuAccess(['Portal.Admin.Agreements.MenuAccess']);
  hasPartnersPermission = this.identityStore.hasPermissionForMenuAccess(['Portal.Admin.Partners.MenuAccess']);
  hasCustomersPermission = this.identityStore.hasPermissionForMenuAccess(['Portal.Admin.Customers.MenuAccess']);

  navExpandAuthorization: NavExpandNode[] = setupAuthorizationMenu(this.identityStore);
  navExpandActions: NavExpandNode[] = setupActionsMenu(this.identityStore);
  navExpandMarketing: NavExpandNode[] = setupMarketingMenu(this.identityStore);
  navExpandFileManagement: NavExpandNode[] = setupFileManagementMenu(this.identityStore);
  navExpandProductsAndOrders: NavExpandNode[] = setupProductsAndOrdersMenu(this.identityStore);
  navExpandCsp: NavExpandNode[] = setupCspMenu(this.identityStore);
  navExpandNotifications: NavExpandNode[] = setupNotificationsMenu(this.identityStore);
  navExpandCommonData: NavExpandNode[] = setupCommonDataMenu(this.identityStore);
  navExpandMonitoring: NavExpandNode[] = setupMonitoringMenu(this.identityStore);
  navExpandJobOrchestrator: NavExpandNode[] = setupJobOrchestratorMenu(this.identityStore);

  constructor() {
    effect(() => {
      const expires_at = this.identityStore.tokenExpiration();
      this.initRefreshTokenPolling(expires_at);
    });
  }

  onLinkClicked({ customAction }: NavClickEvent): void {
    if (customAction) {
      this.handleCustomActions(customAction);
    }
  }

  logout(): void {
    this.identityStore.logout();
  }

  onNavArrowKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const handles = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('cui-side-nav-container a.handle')).filter(
      handle => handle.offsetParent !== null,
    );
    const currentIndex = handles.indexOf(document.activeElement as HTMLElement);
    if (currentIndex < 0) return;

    keyboardEvent.preventDefault();
    const direction = keyboardEvent.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + direction + handles.length) % handles.length;
    handles[nextIndex].focus();
  }

  private handleCustomActions(type: string): void {
    if (type === 'MpnIdCheck') {
      this.dialogService.openDialogWithoutUnsavedChangesCheck(MpnIdCheckDialogComponent, {
        width: '600px',
      });
    }
    if (type === 'LicenseKeyGeneration') {
      this.dialogService.openDialogWithoutUnsavedChangesCheck(GenerateLicenseKeyDialogComponent, {
        width: '600px',
      });
    }
  }

  private initRefreshTokenPolling(expires_at: number): void {
    const currentTime = Date.now() / 1000; // Current time in seconds
    const timeUntilExpiration = expires_at - currentTime; // Time until expiration in seconds
    if (timeUntilExpiration > 300) {
      const refreshTimeout = (timeUntilExpiration - 300) * 1000;
      setTimeout(() => {
        this.identityStore.refresh();
      }, refreshTimeout);
    }
  }
}
